import { forwardRef, useImperativeHandle, useState } from 'react';

import debounce from 'lodash/debounce';

import type { AlertProps } from '@packages/fenrir/components';
import { Alert } from '@packages/fenrir/components';
import type { SnackbarProps } from '@packages/fenrir/components/snackbar';
import { Snackbar } from '@packages/fenrir/components/snackbar';

// Define the interface for the ref
export interface AssetsSnackbarRef {
  show: () => void;
  hide: () => void;
  getMessage: () => string | undefined;
  setMessage: (msg: string) => void;
  setSeverity: (severity: AlertProps['severity']) => void;
  setVariant: (variant: AlertProps['variant']) => void;
  showCustomAlert: (props: CustomAlertProps) => void;
}

const SNACKBAR_AUTO_HIDE_DURATION = 5000;
const SNACKBAR_ANCHOR_ORIGIN: SnackbarProps['anchorOrigin'] = {
  vertical: 'top',
  horizontal: 'right',
};
interface AssetsSnackbarProps {
  defaultMessage: string;
  defaultVariant: AlertProps['variant'];
  defaultSeverity: AlertProps['severity'];
}

interface CustomAlertProps {
  message?: string;
  variant?: AlertProps['variant'];
  severity?: AlertProps['severity'];
}

/**
 * AssetsSnackbar component displays an alert message in a snackbar.
 * It can be controlled externally via a ref.
 * It uses a debounce to prevent rapid show/hide actions.
 * The message can be set via props or while showing alert using showCustomAlert function.
 *
 * @param {string} defaultMessage - The default message to display in the snackbar.
 * @param {string} [defaultVariant='standard'] - The default variant of the snackbar. defaults to 'standard'.
 * @param {string} [defaultSeverity='error'] - The default severity of the snackbar. defaults to 'error'.
 * @returns {JSX.Element} The rendered AssetsSnackbar component.
 *
 * @example
 * const assetsSnackbarRef = useRef<AssetsSnackbarRef>(null);
 *
 * <AssetsSnackbar ref={AssetsSnackbarRef} defaultMessage="An error occurred" />
 * // assetsSnackbarRef.current?.show();
 * // assetsSnackbarRef.current?.hide();
 * // assetsSnackbarRef.current?.setMessage("Custom error message");
 * // assetsSnackbarRef.current?.showCustomError("Another custom success message");
 */
export const AssetsSnackbar = forwardRef<
  AssetsSnackbarRef,
  AssetsSnackbarProps
>(({ defaultMessage, defaultVariant, defaultSeverity }, ref) => {
  const [alertMessage, setAlertMessage] = useState(defaultMessage);
  const [variant, setVariant] = useState(defaultVariant);
  const [severity, setSeverity] = useState(defaultSeverity);
  const [showError, setShowError] = useState<boolean>(false);

  const debouncedShowAlert = debounce(() => {
    setShowError(true);
  }, 300);

  useImperativeHandle(ref, () => ({
    show: debouncedShowAlert,
    hide: () => setShowError(false),
    getMessage: () => alertMessage,
    setMessage: (msg: string) => {
      setAlertMessage(msg);
    },
    setSeverity: (severity: AlertProps['severity']) => {
      setSeverity(severity ?? defaultSeverity);
    },
    setVariant: (variant: AlertProps['variant']) => {
      setVariant(variant ?? defaultVariant);
    },
    showCustomAlert: (props: CustomAlertProps) => {
      if (props.message) {
        setAlertMessage(props.message);
      }
      if (props.severity) {
        setSeverity(props.severity);
      }
      if (props.variant) {
        setVariant(props.variant);
      }
      debouncedShowAlert();
    },
  }));

  return (
    <Snackbar
      open={showError}
      autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
      onClose={() => setShowError(false)}
      anchorOrigin={SNACKBAR_ANCHOR_ORIGIN}
    >
      <Alert
        variant={variant}
        severity={severity}
        data-testid="assets-snackbar-alert"
        onClose={() => setShowError(false)}
      >
        {alertMessage}
      </Alert>
    </Snackbar>
  );
});

AssetsSnackbar.displayName = 'AssetsSnackbar';
